import type { CSSProperties, ReactNode } from "react";
import { colors, fontSize, radius } from "../lib/tokens";
import { SearchIcon } from "./icons";
import { Wordmark } from "./Wordmark";

interface TopbarProps {
  breadcrumbs: ReactNode;
  /** When true, renders a compact mobile bar with hamburger button instead of search. */
  mobile?: boolean;
  /** Called when hamburger is tapped (mobile only). */
  onToggleMenu?: () => void;
}

function HamburgerIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

export function Topbar({ breadcrumbs, mobile = false, onToggleMenu }: TopbarProps) {
  const bar: CSSProperties = {
    height: 56,
    borderBottom: `1px solid ${colors.fog}`,
    background: colors.paper,
    display: "flex",
    alignItems: "center",
    padding: mobile ? "0 12px" : "0 32px",
    gap: mobile ? 8 : 16,
    position: "sticky",
    top: 0,
    zIndex: 10,
  };
  const search: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 32,
    background: colors.paper,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    padding: "0 10px",
    width: 240,
  };
  const burger: CSSProperties = {
    width: 36,
    height: 36,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: 0,
    borderRadius: radius.sm,
    color: colors.ink,
    cursor: "pointer",
  };
  return (
    <div style={bar}>
      {mobile && (
        <button onClick={onToggleMenu} aria-label="Abrir menu" style={burger}>
          <HamburgerIcon />
        </button>
      )}
      {mobile && (
        <>
          <Wordmark variant="ink" height={20} />
          <span aria-hidden="true" style={{ width: 1, height: 18, background: colors.fog, flexShrink: 0 }} />
        </>
      )}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: fontSize.md,
        color: colors.stone,
        flex: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
      }}>
        {breadcrumbs}
      </div>
      {!mobile && (
        <div style={search}>
          <SearchIcon width={14} height={14} style={{ color: colors.stone }} />
          <input
            type="text"
            placeholder="Buscar tenant, fatura, ação..."
            style={{ border: 0, outline: 0, background: "transparent", flex: 1, font: "inherit", fontSize: fontSize.md, color: colors.ink }}
          />
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: colors.stone, border: `1px solid ${colors.fog}`, borderRadius: radius.xs, padding: "1px 5px", background: colors.paper }}>
            ⌘K
          </span>
        </div>
      )}
    </div>
  );
}

export function Breadcrumb({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <>
      {items.map((it, i) => {
        const isLast = i === items.length - 1;
        if (isLast) return <span key={i} style={{ color: colors.ink, fontWeight: 500 }}>{it.label}</span>;
        return (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {it.to ? <a href={it.to} style={{ color: "inherit", textDecoration: "none" }}>{it.label}</a> : it.label}
            <span style={{ color: colors.stoneSoft }}>/</span>
          </span>
        );
      })}
    </>
  );
}
